import { ref, watch } from "vue";
import { isMobileApp, triggerMobileAppEvent } from "@sazka/mobile-apps/mobile-app-integration";
import { BenPlayerApi } from "@/api-connector/ben-be-player/methods/ben-player-api";
import { PlayerApi } from "@/api-connector/ls-admin-public/metods/player-api";
import { fetchPlayerExtraGameTickets } from "@/api-connector/sazka-api/methods/kasa-apis";
import { UserDataModel } from "./models/user-data-model";
import useUserAccount, { isUserLoggedIn, clientId as userClientId } from "./use-user-account";
import { defineStore } from "pinia";
import { logException } from "@sazka/utils/logger/use-app-logger";
import { PlayerAllwynDraw } from "@/api-connector/ls-admin-public/models/allwyn-draw-models";

const defaultDataValues: UserDataModel = {
    availableSpins: 0,
    draws: null,
    withdrawableAmount: 0,
    withdrawalExpiration: "",
    tickets: 0,
    hasMaximumTickets: false,
    prize: "",
    drawDate: "",
    informationPageUrl: "",
    image: "",
};

export const useUserData = defineStore("user-data", () => {
    const userData = ref<UserDataModel>(defaultDataValues);
    const allwynDrawData = ref<PlayerAllwynDraw>(null);

    const { waitForUserAccount } = useUserAccount();

    const loadUserData = async () => {
        if (isMobileApp) {
            triggerMobileAppEvent("refreshBalance");
            return;
        }

        try {
            await waitForUserAccount();

            if (!userClientId.value) {
                return;
            }
            const accountStatusPromise = PlayerApi.fetchPlayerAccountStatus(userClientId.value);
            const extraGameTicketsPromise = fetchPlayerExtraGameTickets();
            const playerWalletPromise = BenPlayerApi.fetchPlayerWallet(userClientId.value);

            const [accountStatus, playerWallet, extraGameTickets] = await Promise.all([
                accountStatusPromise,
                playerWalletPromise,
                extraGameTicketsPromise,
            ]);

            userData.value = { ...accountStatus, ...playerWallet, ...extraGameTickets };
        } catch (err: unknown) {
            logException("user-data", err);
        }
    };

    const loadAllwynDrawData = async () => {
        await waitForUserAccount();

        if (!userClientId.value) {
            return;
        }

        try {
            allwynDrawData.value = await PlayerApi.fetchPlayerAllwynDraw(userClientId.value);
        } catch (err: unknown) {
            logException("allwyn-draw", err);
        }
    };

    const loadAllUserData = async () => {
        const userDataPromise = loadUserData();
        const allwynDataPromise = loadAllwynDrawData();

        await Promise.all([userDataPromise, allwynDataPromise]);
    };

    watch(
        isUserLoggedIn,
        async (newIsUserLoggedIn) => {
            if (newIsUserLoggedIn) {
                await loadAllUserData();
            }
        },
        { immediate: true }
    );

    return {
        userData,
        allwynDrawData,
        loadUserData,
        loadAllwynDrawData,
        loadAllUserData,
    };
});
